/**
 * Схема валидации формы оплаты для неавторизованного пользователя (гостя).
 *
 * Схема зависит от режима идентификации счёта (по телефону или по номеру ЛС)
 * и от признака годовой оплаты (повышенный лимит суммы).
 */

import * as yup from 'yup';
import { config } from '../../../config';

/** Режим идентификации лицевого счёта в форме гостя. */
export type GuestIdentifyBy = 'phone' | 'accountNumber';

/** Параметры построения схемы валидации формы гостя. */
export interface GuestPaymentSchemaOptions {
  /** Чем клиент идентифицирует счёт: телефоном или номером лицевого счёта */
  identifyBy: GuestIdentifyBy;
  /** Годовая оплата по акции: максимальная сумма берётся из `annualMaxAmount` */
  annual: boolean;
}

/** Формат телефона после применения маски `+7 (999) 999-99-99`. */
export const GUEST_PHONE_MASKED_REGEX = /^\+7\s\(\d{3}\)\s\d{3}-\d{2}-\d{2}$/;

/**
 * Возвращает максимальную сумму одного платежа гостя.
 *
 * @param annual - признак годовой оплаты
 * @returns `config.payment.annualMaxAmount` для годовой оплаты, иначе `config.payment.maxAmount`
 */
export const getGuestMaxAmount = (annual: boolean): number =>
  annual ? config.payment.annualMaxAmount : config.payment.maxAmount;

/**
 * Строит схему валидации формы оплаты гостя.
 *
 * - режим `phone`: телефон обязателен и должен соответствовать маске, номер ЛС не проверяется;
 * - режим `accountNumber`: номер ЛС обязателен и проверяется по `config.payment.accountNumberPattern`,
 *   телефон не обязателен;
 * - сумма: от `config.payment.minAmount` до `maxAmount` (или `annualMaxAmount` при `annual`);
 * - email необязателен, но при заполнении должен быть корректным.
 *
 * @param options - режим идентификации и признак годовой оплаты
 * @returns yup-схема для resolver react-hook-form
 *
 * @example
 * const schema = buildGuestPaymentSchema({ identifyBy: 'accountNumber', annual: true });
 */
export const buildGuestPaymentSchema = ({ identifyBy, annual }: GuestPaymentSchemaOptions) => {
  const maxAmount = getGuestMaxAmount(annual);
  const { currencySymbol, minAmount } = config.payment;

  return yup.object({
    phone: yup
      .string()
      .default('')
      .test('phone-required', 'Введите номер телефона', (value) =>
        identifyBy !== 'phone' || (value ?? '').trim() !== ''
      )
      .test('phone-format', 'Неверный формат телефона', (value) =>
        identifyBy !== 'phone' || (value ?? '').trim() === '' || GUEST_PHONE_MASKED_REGEX.test(value ?? '')
      ),
    accountNumber: yup
      .string()
      .default('')
      .test('account-number-required', 'Введите номер лицевого счёта', (value) =>
        identifyBy !== 'accountNumber' || (value ?? '').trim() !== ''
      )
      .test('account-number-format', 'Неверный формат номера лицевого счёта', (value) =>
        identifyBy !== 'accountNumber' ||
        (value ?? '').trim() === '' ||
        config.payment.accountNumberPattern.test((value ?? '').trim())
      ),
    amount: yup
      .number()
      .typeError('Введите сумму')
      .required('Введите сумму')
      .min(minAmount, `Минимальная сумма ${minAmount} ${currencySymbol}`)
      .max(maxAmount, `Максимальная сумма ${maxAmount} ${currencySymbol}`),
    email: yup.string().email('Неверный формат email').notRequired(),
  });
};

/** Тип данных формы оплаты гостя после валидации (одинаков для обоих режимов). */
export type GuestPaymentFormData = yup.InferType<ReturnType<typeof buildGuestPaymentSchema>>;

/** Значения полей формы оплаты гостя (вход resolver react-hook-form). */
export interface GuestPaymentFormValues {
  /** Телефон с маской `+7 (999) 999-99-99` (пустая строка, если не вводился) */
  phone: string;
  /** Номер лицевого счёта (пустая строка, если не вводился) */
  accountNumber: string;
  /** Сумма платежа, ₽ (`undefined`, пока поле пустое) */
  amount: number;
  /** Email для чека */
  email: string;
}
